import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';

import { getCore } from '../../../../client/core/getCore';
import { getDefaultClient } from '../../../../client/defaultClient';
import { NoWebAuthNSupportError } from '../../../../errors/NoWebAuthNSupportError';
import { updateAuthFromVerifyResponse } from '../../updateAuthFromVerifyResponse';
import { getPasskeyAuthenticationSignInOptions } from '../getPasskeyAuthenticationSignInOptions';
import { serverSigninPasskey } from '../serverSigninPasskey';

export type SignInWithPasskeyParams = {
  relatedOriginRpId?: string;
};

/**
 * Signs in the user using a registered passkey.
 *
 * This function authenticates the user with a previously registered WebAuthn
 * credential, providing passwordless authentication using biometrics or
 * security keys.
 *
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to the authentication response.
 * @throws NoWebAuthNSupportError If WebAuthn is not supported by the browser.
 */
export const signInWithPasskey = async (
  { relatedOriginRpId }: SignInWithPasskeyParams = {},
  client = getDefaultClient()
) => {
  const core = getCore(client);
  if (!core.passkey.isSupported()) {
    throw new NoWebAuthNSupportError();
  }

  const options = await getPasskeyAuthenticationSignInOptions(
    { relatedOriginRpId },
    client
  );

  const formattedOptions: PublicKeyCredentialRequestOptionsJSON = {
    ...options,
  };

  const authentication = await core.passkey.authenticate({
    optionsJSON: formattedOptions,
  });

  const response = await serverSigninPasskey({ authentication }, client);

  updateAuthFromVerifyResponse({ response }, client);

  return response;
};
